import { type JSX, useCallback, useMemo } from "react";
import parse, {
  type DOMNode,
  domToReact,
  Element,
  type HTMLReactParserOptions,
} from "html-react-parser";

import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

export const DYNAMIC_FOOTNOTE_ATTRIBUTE = "data-footnote";

type Props = {
  domNode: Element;
  options: HTMLReactParserOptions;
};

export function DynamicFootnote({ domNode }: Props) {
  const footnoteOrder = domNode.attribs[DYNAMIC_FOOTNOTE_ATTRIBUTE];
  const refId = `footnote-ref-${footnoteOrder}`;
  const slotId = `footnote-slot-${footnoteOrder}`;

  return (
    <a
      className="cursor-pointer"
      href={`#${slotId}`}
      id={refId}
      key={footnoteOrder}
    >
      <span>
        ({m.common_footnote_see()} {footnoteOrder})
      </span>
    </a>
  );
}

export type Footnotes = (JSX.Element | JSX.Element[] | string)[];

export function useFootnotes(
  initialFootnotes?: { id: number; text: string }[],
) {
  const footnotes = useMemo<Footnotes>(() => {
    const array: string[] = [];
    initialFootnotes?.forEach(
      (footnote) => (array[footnote.id] = footnote.text),
    );

    return array;
  }, [initialFootnotes]);

  const parseFootnotes = useCallback(
    (text: string) => {
      parse(text, {
        replace(domNode) {
          if (
            domNode instanceof Element &&
            isTruthy(domNode.attribs[DYNAMIC_FOOTNOTE_ATTRIBUTE])
          ) {
            const order = domNode.attribs[DYNAMIC_FOOTNOTE_ATTRIBUTE];

            if (!order) throw new Error("No order found for footnote");
            footnotes[Number(order)] = domToReact(
              domNode.children as DOMNode[],
            );
          }
        },
      });

      return text;
    },
    [footnotes],
  );

  return {
    footnotes,
    parseFootnotes,
  };
}
